import { catchError, map } from 'rxjs/operators';
import { ICrudForm } from '../form/crud-form.interface';
import { FormGroup } from '@angular/forms';
import { Observable, Subject, Subscription, throwError } from 'rxjs';
import { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';

import { FormEvent } from '../form/form-event.enum';
import { IErrorResponse, IFieldError } from '../form/error-response';
import { ValidatorMapper } from '../form/validator-mapper';
import { UrlBuilder } from '../api/url-builder';
import { IConfirmationDialogPayload } from '../../ui/component/confirmation-dialog/confirmation-dialog-payload.interface';
import { flatValidationErrors } from '../../util/flat-validation-errors';
import { ErrorCodes } from '../../enum/api-errors';

export abstract class AbstractFormComponent implements OnInit, AfterViewInit, OnDestroy {

  protected record: any = {};
  protected loading: boolean = false;
  protected loadingPromise: Subscription;
  protected formMode: 'create' | 'update';
  protected savePromise: Subscription;
  protected form: FormGroup;
  protected channel: Subject<FormEvent> = new Subject<FormEvent>();
  protected submitListener: Subject<any> = new Subject<any>();
  /**
   * Http operation subject, for confirmation dialog
   * @type {Subject<IConfirmationDialogPayload>}
   */
  protected deleteSubject: Subject<IConfirmationDialogPayload> = new Subject();

  protected abstract getUrl(forceId?: number): string;

  protected abstract onSubmit(data: any): void;

  constructor(protected formBuilder: ICrudForm,
              protected httpClient: HttpClient,
              protected toastrService: ToastrService,
              protected router: Router,
              protected route: ActivatedRoute,
              protected urlBuilder?: UrlBuilder) {
  }

  ngOnInit(): void {
    this.submitListener.subscribe(
      (response: any) => this.onSubmit(response)
    );
    this.formMode = (this.route.snapshot.params.id) ? 'update' : 'create';
    this.form = this.formBuilder.buildForm(this.formMode === 'update');
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    if (this.loadingPromise) {
      this.loadingPromise.unsubscribe();
    }
    if (this.submitListener) {
      this.submitListener.unsubscribe();
    }
    if (this.savePromise) {
      this.savePromise.unsubscribe();
    }
    if (this.channel) {
      this.channel.unsubscribe();
    }
  }

  populate(forceId?: number): Observable<any> {
    this.loading = true;
    let url = this.getUrl(forceId);
    if (this.urlBuilder) {
      url = this.urlBuilder.buildUrl(url);
    }
    return this.httpClient.get<any>(url).pipe(
      map((response: any) => {
        this.record = response;
        this.form.patchValue(this.record);

        this.loading = false;
        return response;
      }),
      catchError((error: any) => {
        this.loading = false;
        return throwError(error);
      }));
  }

  getFormData() {
    return this.form.value;
  }

  submit(): void {
    this.channel.next(FormEvent.SUBMIT);
    if (this.form.valid) {
      let url = this.getUrl(this.record.id);
      if (this.urlBuilder) {
        url = this.urlBuilder.buildUrl(url);
      }
      const method = this.formMode === 'create' ? this.httpClient.post : this.httpClient.put;
      this.channel.next(FormEvent.SAVE);
      this.savePromise = method.call(this.httpClient, url, this.getFormData())
        .subscribe((response: any) => {
          // update the record before notifying listeners, so onSubmit handlers
          // (e.g. ones reloading by this.record.id) see the persisted id
          Object.assign(this.record, response);
          this.formMode = 'update';
          this.submitListener.next(response);
          return response;
        }, (error: any) => {
          this.bindFormErrorFromResponse(<IErrorResponse>(error.error));
          console.log(error);
          return error;
        });
    } else {
      this.toastrService.error('Form contains errors', '', {
        enableHtml: true
      });
    }
  }

  bindFormErrorFromResponse(error: IErrorResponse): void {
    if (!error) {
      return;
    }
    if (error.errors) {
      error.errors.forEach((fieldError: IFieldError) => {
        if (fieldError.constraints) {
          this.form.controls[fieldError.field].setErrors(
              ValidatorMapper.map(fieldError.constraints)
          );
        }
      });
    }
    if (error.errors) {
      this.toastrService.error((error.message + flatValidationErrors(error.errors)),  '',{
        enableHtml: true
      });
    }
    if (error.message && !error.errors) {
      this.toastrService.error(error.message,  '',{
        enableHtml: true
      });
    }
    if (error.code) {
      this.toastrService.error(ErrorCodes[error.code] || error.code, '', {
        enableHtml: true
      });
    }
    if (!error.errors && !error.code && !error.message) {
      this.toastrService.error('Error', '', {
        enableHtml: true
      });
    }
  }
}
